// ADO-3749: the User Menu (Figma 7465:227253, guideline 7465:230130), top right of every
// header: who is signed in, separately from the account the depot switcher names.
//
//   desktop  a white chip, 40px: initials avatar (navy), first name, chevron. The account
//            is not on the chip (Epic 7 3 amigos, 2026-11-02: a long store name pushed the
//            header off the page); it is in the open menu's "Signed in to" band.
//   tablet   the same chip at 44px (touch).
//   mobile   the initials alone, white with navy initials, a 32px avatar in a 44px tap area.
//
// Open, it lists who (40px avatar and full name), "Signed in to" with the active account on
// a grey band, then the actions: "My account" (the way into /account, story DEC-1) and
// Sign out in dark red. A dropdown right-aligned under the chip on desktop and tablet (44px
// rows); a bottom sheet over a scrim on mobile (48px rows), dismissed by a downward swipe on
// its handle, a scrim tap or Esc, with focus kept inside it while it is open.
//
// The trigger is a menu button (`aria-haspopup="menu"`, `aria-expanded`; on mobile
// `aria-haspopup="dialog"`, as it opens the sheet), named "Signed in as {full name},
// account menu". Opening
// moves focus to the first item; the arrow keys, Home and End move between items; Esc closes
// and returns focus to the trigger; Tab leaves the dropdown and closes it.
//
// Controlled: the host owns `open`, so the header can keep one menu open at a time (the depot
// switcher or this one). Presentational: the host supplies the names, the account and the
// sign-out action; the design system reads no store.

import { type KeyboardEvent, type MouseEvent, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { Icon } from '../Icon/Icon';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useSheetDrag } from '../hooks/useSheetDrag';
import type { HeaderLayout } from '../SiteHeader/useHeaderLayout';
import { chipFirstName, userInitials } from './userMenuNames';
import styles from './UserMenu.module.css';

/** The signed-in person. `fullName` is never blank: a host with no name passes `null`. */
export interface UserMenuUser {
  readonly firstName: string;
  readonly lastName: string;
  readonly fullName: string;
}

/** The active account: its line ("Main grocery · 3205484") for the "Signed in to" band. */
export interface UserMenuAccount {
  readonly line: string;
}

export interface UserMenuProps {
  readonly layout: HeaderLayout;
  /** Null before the names are known (or when they could not be read): a neutral chip. */
  readonly user: UserMenuUser | null;
  /** Null with no active account: no "Signed in to" band. */
  readonly account: UserMenuAccount | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The Sign out row's action; the row is not drawn without one. */
  readonly onSignOut?: (() => void) | undefined;
  /** The white branding bar of a themed tenant (Story 6.35). */
  readonly themed?: boolean | undefined;
  readonly className?: string | undefined;
}

/** Where "My account" leads (story DEC-1). */
const MY_ACCOUNT_PATH = '/account';

// Figma SignOut 7465:227256 (Phosphor SignOut), drawn on its 18px grid in currentColor.
const signOutGlyph = (
  <svg width={18} height={18} viewBox="0 0 18 18" fill="currentColor" aria-hidden focusable={false}>
    <path d="M7.58516 15.4959C7.58516 15.6573 7.52105 15.812 7.40693 15.9262C7.29281 16.0403 7.13803 16.1044 6.97664 16.1044H2.10852C1.94713 16.1044 1.79235 16.0403 1.67823 15.9262C1.56411 15.812 1.5 15.6573 1.5 15.4959V2.10852C1.5 1.94713 1.56411 1.79235 1.67823 1.67823C1.79235 1.56411 1.94713 1.5 2.10852 1.5H6.97664C7.13803 1.5 7.29281 1.56411 7.40693 1.67823C7.52105 1.79235 7.58516 1.94713 7.58516 2.10852C7.58516 2.2699 7.52105 2.42468 7.40693 2.5388C7.29281 2.65292 7.13803 2.71703 6.97664 2.71703H2.71703V14.8874H6.97664C7.13803 14.8874 7.29281 14.9515 7.40693 15.0656C7.52105 15.1797 7.58516 15.3345 7.58516 15.4959ZM15.9264 8.37167L12.8838 5.32909C12.7696 5.2149 12.6148 5.15076 12.4533 5.15076C12.2918 5.15076 12.1369 5.2149 12.0228 5.32909C11.9086 5.44327 11.8444 5.59813 11.8444 5.75961C11.8444 5.92109 11.9086 6.07596 12.0228 6.19014L14.0271 8.19368H6.97664C6.81526 8.19368 6.66048 8.25779 6.54636 8.37191C6.43224 8.48603 6.36813 8.6408 6.36813 8.80219C6.36813 8.96358 6.43224 9.11836 6.54636 9.23248C6.66048 9.3466 6.81526 9.41071 6.97664 9.41071H14.0271L12.0228 11.4142C11.9086 11.5284 11.8444 11.6833 11.8444 11.8448C11.8444 12.0063 11.9086 12.1611 12.0228 12.2753C12.1369 12.3895 12.2918 12.4536 12.4533 12.4536C12.6148 12.4536 12.7696 12.3895 12.8838 12.2753L15.9264 9.23272C15.983 9.1762 16.0279 9.10909 16.0585 9.03522C16.0891 8.96135 16.1049 8.88216 16.1049 8.80219C16.1049 8.72222 16.0891 8.64304 16.0585 8.56917C16.0279 8.49529 15.983 8.42818 15.9264 8.37167Z" />
  </svg>
);

/** The menu's items, in order. */
function itemsOf(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(menu?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
}

/** AC17: the trigger's accessible name. */
function userMenuLabel(user: UserMenuUser | null): string {
  return user === null ? 'Account menu' : `Signed in as ${user.fullName}, account menu`;
}

export function UserMenu({
  layout,
  user,
  account,
  open,
  onOpenChange,
  onSignOut,
  themed = false,
  className,
}: UserMenuProps) {
  const mobile = layout === 'mobile';
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const sheetId = useId();
  const infoId = useId();
  // The item the next open focuses: the first, or the last after an ArrowUp on the trigger.
  const focusOnOpenRef = useRef<'first' | 'last'>('first');
  // The host passes an inline arrow; the document listeners read the latest one.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });

  const close = useCallback(() => onOpenChangeRef.current(false), []);
  const initials = user === null ? '' : userInitials(user.firstName, user.lastName);
  const label = userMenuLabel(user);

  const sheetOpen = open && mobile;
  useBodyScrollLock(sheetOpen);
  // However the sheet closes, this cleanup focuses the trigger with `preventScroll`. It is
  // declared before `useFocusTrap`, and React runs effect cleanups in declaration order, so it
  // runs before the trap restores focus.
  useEffect(() => {
    if (!sheetOpen) return;
    const trigger = triggerRef.current;
    return () => trigger?.focus({ preventScroll: true });
  }, [sheetOpen]);
  const trapTab = useFocusTrap(sheetOpen, panelRef);
  const sheetDrag = useSheetDrag(sheetOpen, panelRef, close);

  // AC15: opening moves focus into the menu. On mobile the focus trap moves it to the
  // sheet's first item; the dropdown does it here.
  useEffect(() => {
    if (!open) return;
    const focusLast = focusOnOpenRef.current === 'last';
    focusOnOpenRef.current = 'first';
    if (mobile) return;
    const list = itemsOf(menuRef.current);
    (focusLast ? list[list.length - 1] : list[0])?.focus();
  }, [open, mobile]);

  // AC11: Esc anywhere, and (for the dropdown) a click outside the chip and its panel, close
  // the menu. The sheet sits over a scrim, whose own press closes it.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      // AC16: with focus outside the open sheet (a click on content that takes no focus can
      // leave it on the body), Tab and Shift+Tab come back to its first or last item.
      if (event.key === 'Tab' && mobile) {
        if (panelRef.current?.contains(document.activeElement)) return;
        const list = itemsOf(menuRef.current);
        const target = event.shiftKey ? list[list.length - 1] : list[0];
        if (target === undefined) return;
        event.preventDefault();
        target.focus();
        return;
      }
      if (event.key !== 'Escape') return;
      // Claimed, so a window-level layer does not also dismiss on it.
      event.preventDefault();
      close();
      triggerRef.current?.focus({ preventScroll: true });
    };
    const onOutsideClick = (event: globalThis.MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    if (!mobile) document.addEventListener('click', onOutsideClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onOutsideClick);
    };
  }, [open, mobile, close]);

  const onTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Safari does not focus a clicked button; the sheet's focus trap returns focus to
    // whatever held it at open, which must be the trigger. The trigger is on screen (it was
    // just clicked), so the page must not scroll to align it.
    event.currentTarget.focus({ preventScroll: true });
    onOpenChange(!open);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    event.preventDefault();
    focusOnOpenRef.current = event.key === 'ArrowUp' ? 'last' : 'first';
    onOpenChange(true);
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = itemsOf(menuRef.current);
    const at = list.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => {
      event.preventDefault();
      list[(index + list.length) % list.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(at + 1);
    else if (event.key === 'ArrowUp') move(at <= 0 ? list.length - 1 : at - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(list.length - 1);
    // Tab leaves a dropdown menu (focus moves on as usual). The sheet keeps it (AC16): its
    // items are out of the page's tab order, so the browser's own Tab would leave the sheet
    // from any item, and the sheet moves it from item to item instead.
    else if (event.key === 'Tab' && !mobile) close();
    else if (event.key === 'Tab')
      move(event.shiftKey ? (at <= 0 ? list.length - 1 : at - 1) : at + 1);
  };

  const who =
    user === null ? null : (
      <div className={styles.who} data-part="who">
        <span className={styles.whoAvatar} aria-hidden>
          {initials === '' ? <Icon name="user" size={20} /> : initials}
        </span>
        <span className={styles.whoName}>{user.fullName}</span>
      </div>
    );
  const band =
    account === null ? null : (
      <div className={styles.band}>
        <span className={styles.bandLabel}>Signed in to</span>
        <span className={styles.bandAccount}>{account.line}</span>
      </div>
    );
  const hasInfo = who !== null || band !== null;

  const panelBody = (
    <>
      {hasInfo ? (
        <div id={infoId}>
          {who}
          {band}
        </div>
      ) : null}
      <div
        ref={menuRef}
        role="menu"
        id={menuId}
        tabIndex={-1}
        aria-label="Account menu"
        aria-describedby={hasInfo ? infoId : undefined}
        className={styles.menu}
        onKeyDown={onMenuKeyDown}
      >
        <Link
          to={MY_ACCOUNT_PATH}
          role="menuitem"
          tabIndex={-1}
          className={styles.item}
          onClick={close}
        >
          <Icon name="user" size={18} />
          <span>My account</span>
        </Link>
        {onSignOut ? (
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={`${styles.item} ${styles.signOut}`}
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            {signOutGlyph}
            <span>Sign out</span>
          </button>
        ) : null}
      </div>
    </>
  );

  const panel = !open ? null : mobile ? (
    createPortal(
      <div className={styles.sheetLayer}>
        <div className={styles.scrim} data-testid="user-menu-scrim" aria-hidden onClick={close} />
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the keydown only keeps Tab inside the sheet (AC16); its items are the interactive elements */}
        <div
          ref={panelRef}
          className={styles.sheet}
          data-testid="user-menu-panel"
          data-presentation="sheet"
          id={sheetId}
          // A modal sheet over a scrim; focusable itself, so a click on its text keeps focus
          // inside it.
          role="dialog"
          aria-modal="true"
          aria-label="Account menu"
          tabIndex={-1}
          // The menu moves Tab between its items itself; the trap covers the rest of the sheet.
          onKeyDown={(event) => {
            if (!event.defaultPrevented) trapTab(event);
          }}
        >
          <div className={styles.handleRow} data-testid="user-menu-handle" {...sheetDrag}>
            <span className={styles.handle} />
          </div>
          {panelBody}
        </div>
      </div>,
      document.body,
    )
  ) : (
    <div
      ref={panelRef}
      className={styles.dropdown}
      data-testid="user-menu-panel"
      data-presentation="dropdown"
    >
      {panelBody}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={className ? `${styles.root} ${className}` : styles.root}
      data-themed={themed ? 'true' : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        className={mobile ? styles.avatarButton : styles.chip}
        data-layout={layout}
        // On mobile the popup is the modal sheet (a dialog holding the menu), so the trigger
        // advertises a dialog there and a menu elsewhere.
        aria-haspopup={mobile ? 'dialog' : 'menu'}
        aria-expanded={open}
        aria-controls={open ? (mobile ? sheetId : menuId) : undefined}
        aria-label={label}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
      >
        {mobile ? (
          <span className={styles.mobileAvatar} aria-hidden>
            {initials === '' ? <Icon name="user" size={16} /> : initials}
          </span>
        ) : (
          <>
            <span className={styles.avatar} aria-hidden>
              {initials === '' ? <Icon name="user" size={16} /> : initials}
            </span>
            <span className={styles.nameBlock} aria-hidden>
              <span className={styles.firstName}>
                {user === null ? 'Account' : chipFirstName(user.firstName || user.fullName)}
              </span>
            </span>
            <Icon
              name="caretDown"
              size={16}
              data-part="chevron"
              className={open ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
            />
          </>
        )}
      </button>
      {panel}
    </div>
  );
}
